<template>
  <info-panel
    v-if="simulation"
    :item="simulation"
    :title="simulation?.title"
    :sections="sections"
    :extra-settings="extraSettings"
    @update:sections="updateSections($event)"
    @update:extra-settings="updateExtraSettings($event)"
    @close="emit('close')"
  >
    <!-- SETTINGS -->
    <template #label-showEmptySections>{{ $t("labels.showEmptySections") }}</template>
    
    <template #label-infos>
      {{ $t("headings.simulationInformation") }}
    </template>
    <template #infos>
      <formatted-read-more-text :value="simulation.description" />
      <p>
        {{ $t("objectNames.simulation") }}
        <span v-if="simulation.complete">{{ $t("labels.complete") }}</span>
        <span v-if="!simulation.complete">{{ $t("labels.running") }}</span
        >.
      </p>
      <div>
        {{
          $t("labels.xRoundsPlayed", [
            `${playedRoundsCount}/${simulation.maxRounds}`
          ])
        }}
      </div>
      <div>
        {{
          $t("texts.roundName", {
            roundName: $t(
              `roundUnits.${simulation.roundUnit}`,
              simulation.roundLength
            ),
            roundLength: simulation.roundLength
          })
        }}
      </div>
    </template>

    <!-- SCENARIO -->
    <template #label-scenario>
      {{ $t("objectNames.scenario") }}
    </template>
    <template #scenario v-if="settingsValues.showEmptySections || scenario">
      <button-item v-if="scenario" @click="openOverlay(scenario)">
        {{ scenario.title }}
      </button-item>
      <p v-else class="message mt-0">
        {{ $t("messages.simulationHasNoScenario") }}
      </p>
    </template>

    <template #label-protocol>
      {{ $t("headings.simulationProtocol") }}
    </template>
    <template #label-_actions>
      {{ $t("objectNames.action", 4) }}
    </template>
    <template #label-_incidents>
      {{ $t("objectNames.incident", 4) }}
    </template>
    <template #protocol v-if="settingsValues.showEmptySections || actionsByRoundCount > 0 || incidentsByRoundCount > 0">
      <div class="list">
        <div
          v-for="round in playedRounds"
          :key="round.index"
        >
          <div class="subtitle simulation-infopanel__roundtitle">
            {{ formatRoundIndex(round.index) }}
          </div>

          <div
            v-for="pendingEffect in (pendingActionEffectsByRound[round.index] || [])"
            class="line simulation-infopanel__impulseeffectline"
          >
            <div>
              <div class="label">{{ $t("objectNames.delayed_action", 1) }}</div>
              <button-item>
                <store-item
                  :id="pendingEffect.id"
                  v-slot="{ item: action }"
                  type="action"
                >
                  <span>{{ action.title }}</span>
                  <div class="round-menu__pendingeffectdate">
                    {{ $t("labels.playedIn") }}
                    {{ formatRoundIndex(pendingEffect.roundIndex) }}
                  </div>
                </store-item>
              </button-item>
            </div>
            
            <impulse-source-info
              class="simulation-infopanel__impulseeffects"
              :item="pendingEffect"
              :simulation-id="simulation.id"
              :round-index="pendingEffect.roundIndex"
              :round-offset="pendingEffect.roundOffset"
              :show-title="false"
            />
          </div>

          <div
            v-for="pendingEffect in (pendingIncidentEffectsByRound[round.index] || [])"
            class="line simulation-infopanel__impulseeffectline"
          >
            <div>
              <div class="label">{{ $t("objectNames.delayed_incident", 1) }}</div>
              <button-item>
                <store-item
                  :id="pendingEffect.id"
                  v-slot="{ item: incident }"
                  type="incident"
                >
                  <span>{{ incident.title }}</span>
                  <div class="round-menu__pendingeffectdate">
                    {{ $t("labels.activatedIn") }}
                    {{ formatRoundIndex(pendingEffect.roundIndex) }}
                  </div>
                </store-item>
              </button-item>
            </div>

            <impulse-source-info
              class="simulation-infopanel__impulseeffects"
              :item="pendingEffect"
              :simulation-id="simulation.id"
              :round-index="pendingEffect.roundIndex"
              :round-offset="pendingEffect.roundOffset"
              :show-title="false"
            />
          </div>

          <template v-if="sections._incidents">
            <div
              v-for="item in incidentsByRound[round.index]"
              class="line simulation-infopanel__impulseeffectline"
            >
              <div>
                <div class="label">{{ $t("objectNames.incident") }}</div>
                <button-item @click="openOverlay(item)">
                  {{ item.title }}
                </button-item>
              </div>
              
              <impulse-source-info
                class="simulation-infopanel__impulseeffects"
                :item="item"
                :simulation-id="simulation.id"
                :round-index="round.index"
                :show-title="false"
              />
            </div>
          </template>

          <template v-if="sections._actions">
            <div
              v-for="item in actionsByRound[round.index]"
              class="line simulation-infopanel__impulseeffectline"
            >
              <div>
                <div class="label">{{ $t("objectNames.action") }}</div>
                <button-item @click="openOverlay(item)">
                  {{ item.title }}
                </button-item>
              </div>
              
              <impulse-source-info
                class="simulation-infopanel__impulseeffects"
                :item="item"
                :simulation-id="simulation.id"
                :round-index="round.index"
                :show-title="false"
              />
            </div>
          </template>
        </div>
      </div>
    </template>

    <template #label-export>
      {{ $t("headings.export") }}
    </template>
    <template #export>
      <button class="button mt-2" type="button" @click="downloadExport">
        {{ $t("labels.exportSimulationData") }}
      </button>
    </template>
  </info-panel>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { saveAs } from "file-saver";
import { mapValues } from "lodash-es";

import InfoPanel from "@editor/ui/InfoPanel.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";

import StoreItem from "@editor/items/StoreItem.vue";
import ImpulseSourceInfo from "@editor/items/impulse/ImpulseSourceInfo.vue";

import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";

import { formatRoundName } from "@editor/items/simulationRound/useRoundTime";

import type { InfoPanelSetting } from "@editor/store/infoPanels.store";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";
import type { SimulationItem } from "./SimulationItem";

const props = defineProps<{
  simulation: SimulationItem | null;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: OverlayDialogItemRequest): void;
}>();

const infoPanelStore = useInfoPanelStore();

const sections = reactive({
  infos: true,
  scenario: true,
  protocol: true,
  _incidents: true,
  _actions: true,
  export: false
});

const extraSettings = reactive({
  showEmptySections: { type: "check" as const, value: true }
});

const scenario = computed(() => {
  if (props.simulation == null) return null;
  if (props.simulation.scenarioId == null) return null;

  return useScenarioStore().items[props.simulation.scenarioId];
});

// === SIMULATION VARS === //

const playedRoundsCount = computed(() => {
  if (props.simulation == null) return 0;
  const index = props.simulation?.currentRoundIndex;
  if (props.simulation.complete) return index + 1;
  return index;
});

const playedRounds = computed(() => {
  if (props.simulation == null) return [];
  return props.simulation.rounds.filter((round) => round.played);
});

const actionsByRound = computed(() => {
  if (props.simulation == null) return {};
  return useSimulationStore().getActionsByRound(props.simulation.id);
});
const incidentsByRound = computed(() => {
  if (props.simulation == null) return {};
  return useSimulationStore().getIncidentsByRound(props.simulation.id);
});
const pendingEffectsByRound = computed(() => {
  if (props.simulation == null) return {};
  return useSimulationStore().getPendingEffectsByRound(props.simulation.id);
});
const pendingIncidentEffectsByRound = computed(() => {
  return mapValues(pendingEffectsByRound.value, (pendingEffects) =>
    pendingEffects.filter(
      (pendingEffect) => pendingEffect.type === "incident"
    )
  );
});
const pendingActionEffectsByRound = computed(() => {
  return mapValues(pendingEffectsByRound.value, (pendingEffects) =>
    pendingEffects.filter(
      (pendingEffect) => pendingEffect.type === "action"
    )
  );
});

const actionsByRoundCount = computed(() => {
  return Object.keys(actionsByRound.value).length;
});
const incidentsByRoundCount = computed(() => {
  return Object.keys(incidentsByRound.value).length;
});

function downloadExport() {
  if (props.simulation == null) return;
  const simulationStore = useSimulationStore();
  const exportText = simulationStore.getExportText(props.simulation.id);

  const currentTime = new Date();
  const fileName = `simcision-export-${currentTime.getFullYear()}-${
    currentTime.getMonth() + 1
  }-${currentTime.getDate()}-${currentTime.getHours()}-${currentTime.getMinutes()}.txt`;

  const blob = new Blob([exportText], { type: "text/plain;charset=utf-8" });
  return saveAs(blob, fileName);
}

function formatRoundIndex(roundIndex: number) {
  if (props.simulation == null) return "";
  return formatRoundName(
    props.simulation.durationType,
    props.simulation.roundUnit,
    props.simulation.roundLength,
    roundIndex,
    props.simulation.timeStart
  );
}

function openOverlay(item: OverlayDialogItemRequest) {
  emit("select", item);
}

// SECTIONS

function updateSections(value: { [section: string]: boolean }) {
  Object.assign(sections, value);
  infoPanelStore.sections.simulation = sections;
}

function loadSections() {
  // TODO: add sections depending if feature is disabled / enabled
  const savedSections = infoPanelStore.sections?.simulation || {};
  for (const key in sections) {
    if (key in savedSections) {
      sections[key as (keyof typeof sections)] = savedSections[key];
    }
  }
}

loadSections();


function updateExtraSettings(value: { [setting: string]: InfoPanelSetting }) {
  Object.assign(extraSettings, value);
  infoPanelStore.extraSettings.simulation = extraSettings;
}

function loadExtraSettings() {
  const savedExtraSettings = infoPanelStore.extraSettings?.simulation || {};
  for (const key in extraSettings) {
    if (key in savedExtraSettings) {
      extraSettings[key as (keyof typeof extraSettings)] = { ...(savedExtraSettings[key] as any) };
    }
  }
}

const settingsValues = computed(() => {
  return mapValues(extraSettings, (setting) => setting.value);
});

loadExtraSettings();
</script>

<style lang="scss">
.simulation-infopanel {
  &__impulseeffectline {
    justify-content: space-between;
    margin-bottom: 2rem;
    margin-top: 1.5rem;
    align-items: flex-start;

    & > div:first-child {
      flex: 1;
    }
  }
  &__impulseeffects {
    margin-left: 0.5rem;
  }

  &__roundtitle {
    position: sticky;
    top: 0;
    background-color: var(--color-background-deep);
    z-index: 2;
    padding-left: 1rem;
    padding-block: 0.5rem;
    margin-left: -1rem;
    border-bottom: 1px solid var(--color-border);
    box-shadow: 0 0.5rem 0.75rem -0.75rem rgba(var(--tint-contrast), 0.1);
  }
}
</style>
